import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const SITE_BASE = "https://ninjarunclub.com";

export const api = axios.create({ baseURL: API });

export const IMAGES = {
  hero: "/img/hero.jpg",
  city: "/img/city.jpg",
  training: "/img/training.jpg",
  crew: "/img/crew.jpg",
};

function upsertMeta(selector, create, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.content = content;
}

const namedMeta = (name) => () => Object.assign(document.createElement("meta"), { name });
const propMeta = (property) => () => {
  const m = document.createElement("meta");
  m.setAttribute("property", property);
  return m;
};

const abs = (url) => (url && url.startsWith("http") ? url : `${SITE_BASE}${url}`);

function setCanonical(path) {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = `${SITE_BASE}${path}`;
  upsertMeta('meta[property="og:url"]', propMeta("og:url"), `${SITE_BASE}${path}`);
}

export function setPageMeta(title, description, opts = {}) {
  document.title = title;
  const desc = description || "";
  upsertMeta('meta[name="description"]', namedMeta("description"), desc);
  upsertMeta('meta[property="og:title"]', propMeta("og:title"), title);
  upsertMeta('meta[property="og:description"]', propMeta("og:description"), desc);
  upsertMeta('meta[name="twitter:title"]', namedMeta("twitter:title"), title);
  upsertMeta('meta[name="twitter:description"]', namedMeta("twitter:description"), desc);
  upsertMeta('meta[name="robots"]', namedMeta("robots"), opts.noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
  upsertMeta('meta[property="og:type"]', propMeta("og:type"), opts.type || "website");
  const image = abs(opts.image || "/brand/og-image.jpg");
  upsertMeta('meta[property="og:image"]', propMeta("og:image"), image);
  upsertMeta('meta[property="og:image:alt"]', propMeta("og:image:alt"), opts.imageAlt || title);
  upsertMeta('meta[name="twitter:image"]', namedMeta("twitter:image"), image);
  ["article:published_time", "article:modified_time"].forEach((prop, i) => {
    const val = i === 0 ? opts.published : opts.modified;
    const existing = document.querySelector(`meta[property="${prop}"]`);
    if (val) upsertMeta(`meta[property="${prop}"]`, propMeta(prop), val);
    else if (existing) existing.remove();
  });
  setCanonical(opts.canonicalPath || window.location.pathname);
}

// Audit NRC-002: sync canonical + an interim title the moment the route changes,
// before any data fetch resolves. Pages overwrite with exact meta once loaded.
export function setRouteDefaults(pathname) {
  setCanonical(pathname);
  upsertMeta('meta[name="robots"]', namedMeta("robots"), "index, follow, max-image-preview:large");
  const seg = pathname.split("/").filter(Boolean).pop() || "";
  const interim = pathname === "/"
    ? "Ninja Run Club | Find Run Clubs Near You Across America"
    : `${seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} | Ninja Run Club`;
  document.title = interim;
}

export function setJsonLd(id, ld) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(ld);
  return () => el.remove();
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, url], i) => ({
      "@type": "ListItem",
      position: i + 1,
      name,
      item: `${SITE_BASE}${url}`,
    })),
  };
}

export function clubListItem(c, position) {
  return {
    "@type": "ListItem",
    position,
    item: {
      "@type": "SportsClub",
      name: c.name,
      url: `${SITE_BASE}/clubs/${c.slug}`,
      sport: "Running",
      ...(c.city ? { address: { "@type": "PostalAddress", addressLocality: c.city, ...(c.state ? { addressRegion: c.state } : {}), addressCountry: "US" } } : {}),
    },
  };
}

export const catSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
